import React, { useState, useEffect, useMemo } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import { orderApi } from '../utils/api';
import { stripSarPrice } from '../utils/coursePrice';
import { useUser } from '../contexts/UserContext';

interface Order {
  id: string;
  studentId: string | null;
  studentName: string | null;
  studentPhoneNumber: string | null;
  courseId: string | null;
  courseName: string | null;
  promoCodeId: string | null;
  promoCode: string | null;
  originalPrice: number;
  discountAmount: number;
  netPrice: number;
  orderDate: string;
  status: OrderStatus;
  type: OrderType;
  paymentReference: string | null;
  paymentDate: string | null;
  paymentUrl: string | null;
  paymentSessionExpiresAt: string | null;
}

enum OrderStatus {
  Pending = 1,
  Completed = 2,
  Cancelled = 3,
  Refunded = 4
}

enum OrderType {
  DirectCoursePurchase = 0,
  BuyCoinsDirect = 1,
  WalletChargeByCode = 2,
  WalletCoinsUsage = 3,
  AdminCoinsGrant = 4,
  AdminEnrollment = 5
}

/** API may return enum as number or as string name ("Pending", …). */
function toOrderStatus(raw: unknown): OrderStatus {
  if (typeof raw === 'number' && raw >= 1 && raw <= 4) {
    return raw as OrderStatus;
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    const n = parseInt(trimmed, 10);
    if (!Number.isNaN(n) && n >= 1 && n <= 4) {
      return n as OrderStatus;
    }
    const lower = trimmed.toLowerCase();
    const byName: Record<string, OrderStatus> = {
      pending: OrderStatus.Pending,
      completed: OrderStatus.Completed,
      cancelled: OrderStatus.Cancelled,
      refunded: OrderStatus.Refunded,
    };
    if (byName[lower] !== undefined) {
      return byName[lower];
    }
  }
  return OrderStatus.Pending;
}

function normalizeOrderRow(row: Record<string, unknown>): Order {
  const o = row as unknown as Order;
  return { ...o, status: toOrderStatus(row.status) };
}

interface FilterData {
  studentName: string;
  studentPhoneNumber: string;
  status: string;
  type: string;
  startDate: string;
  endDate: string;
}

interface SortConfig {
  key: keyof Order;
  direction: 'asc' | 'desc';
}

const EXPORT_PAGE_SIZE = 100;

function escapeCsvCell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

function sortOrderList(orders: Order[], sortConfig: SortConfig | null): Order[] {
  const sortableOrders = [...orders];
  if (sortConfig !== null) {
    sortableOrders.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }
  return sortableOrders;
}

const Orders: React.FC = () => {
  const { isModerator } = useUser();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState<FilterData>({
    studentName: '',
    studentPhoneNumber: '',
    status: '',
    type: '',
    startDate: '',
    endDate: '',
  });
  const [appliedFilters, setAppliedFilters] = useState<FilterData>({
    studentName: '',
    studentPhoneNumber: '',
    status: '',
    type: '',
    startDate: '',
    endDate: '',
  });
  const [exporting, setExporting] = useState(false);

  const getOrderStatusLabel = (status: OrderStatus): string => {
    switch (status) {
      case OrderStatus.Pending:
        return 'قيد الانتظار';
      case OrderStatus.Completed:
        return 'مكتمل';
      case OrderStatus.Cancelled:
        return 'ملغي';
      case OrderStatus.Refunded:
        return 'مسترجع';
      default:
        return 'غير معروف';
    }
  };

  const getOrderTypeLabel = (type: OrderType): string => {
    switch (type) {
      case OrderType.DirectCoursePurchase:
        return 'شراء مباشر';
      case OrderType.BuyCoinsDirect:
        return 'شراء محفظة';
      case OrderType.WalletChargeByCode:
        return 'شحن بكود محفظة';
      case OrderType.WalletCoinsUsage:
        return 'استخدام محفظة';
      case OrderType.AdminCoinsGrant:
        return 'الأدمن شحن المحفظة';
      case OrderType.AdminEnrollment:
        return 'تسجيل من الأدمن';
      default:
        return 'غير معروف';
    }
  };

  const getOrderStatusColor = (status: OrderStatus): string => {
    switch (status) {
      case OrderStatus.Completed:
        return 'bg-green-100 text-green-800';
      case OrderStatus.Pending:
        return 'bg-yellow-100 text-yellow-800';
      case OrderStatus.Cancelled:
        return 'bg-red-100 text-red-800';
      case OrderStatus.Refunded:
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getOrderTypeColor = (type: OrderType): string => {
    switch (type) {
      case OrderType.DirectCoursePurchase:
      case OrderType.BuyCoinsDirect:
        return 'bg-blue-100 text-blue-800';
      case OrderType.WalletChargeByCode:
      case OrderType.AdminCoinsGrant:
        return 'bg-green-100 text-green-800';
      case OrderType.WalletCoinsUsage:
        return 'bg-yellow-100 text-yellow-800';
      case OrderType.AdminEnrollment:
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const fetchOrders = async () => {
    if (!isModerator()) {
      setError('ليس لديك صلاحية لعرض الطلبات.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const statusValue = appliedFilters.status ? parseInt(appliedFilters.status) : null;
      const typeValue = appliedFilters.type ? parseInt(appliedFilters.type) : null;
      const response = await orderApi.getAllOrders(
        currentPage,
        itemsPerPage,
        statusValue,
        typeValue,
        appliedFilters.studentName || null,
        appliedFilters.studentPhoneNumber || null,
        appliedFilters.startDate || null,
        appliedFilters.endDate || null
      );

      if (response.success) {
        const orderData = response.data;
        const rows = orderData.data || [];
        setOrders(rows.map((row: Record<string, unknown>) => normalizeOrderRow(row)));
        setTotalCount(orderData.totalCount || 0);
        setTotalPages(orderData.totalPages || 0);
      } else {
        throw new Error(response.message || 'فشل في جلب الطلبات');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('فشل في تحميل الطلبات. يرجى المحاولة مرة أخرى لاحقاً.');
      setOrders([]);
      setTotalPages(0);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isModerator()) {
      fetchOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, appliedFilters, isModerator]);

  const handleSort = (key: keyof Order) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedOrders = useMemo(
    () => sortOrderList(orders, sortConfig),
    [orders, sortConfig]
  );

  const handleSearch = () => {
    setAppliedFilters(filters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      studentName: '',
      studentPhoneNumber: '',
      status: '',
      type: '',
      startDate: '',
      endDate: '',
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleUpdateStatus = async (orderId: string, newStatus: OrderStatus) => {
    try {
      const response = await orderApi.updateOrderStatus(orderId, newStatus);
      if (response.success) {
        setSuccessMessage('تم تحديث حالة الطلب بنجاح');
        fetchOrders();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(response.message || 'فشل في تحديث حالة الطلب');
      }
    } catch (error: any) {
      console.error('Error updating order status:', error);
      setError(error.message || 'فشل في تحديث حالة الطلب. يرجى المحاولة مرة أخرى.');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return `${amount.toFixed(2)} ريال`;
  };

  const handleDownloadCsv = async () => {
    if (!isModerator() || totalCount === 0) return;

    setExporting(true);
    setError(null);
    try {
      const statusValue = appliedFilters.status ? parseInt(appliedFilters.status, 10) : null;
      const typeValue = appliedFilters.type ? parseInt(appliedFilters.type, 10) : null;
      const first = await orderApi.getAllOrders(
        1,
        EXPORT_PAGE_SIZE,
        statusValue,
        typeValue,
        appliedFilters.studentName || null,
        appliedFilters.studentPhoneNumber || null,
        appliedFilters.startDate || null,
        appliedFilters.endDate || null
      );

      if (!first.success) {
        throw new Error(first.message || 'فشل في جلب الطلبات للتصدير');
      }

      const orderData = first.data;
      const allRows: Order[] = (orderData.data || []).map((row: Record<string, unknown>) =>
        normalizeOrderRow(row)
      );
      const pages = orderData.totalPages || 1;

      for (let page = 2; page <= pages; page++) {
        const response = await orderApi.getAllOrders(
          page,
          EXPORT_PAGE_SIZE,
          statusValue,
          typeValue,
          appliedFilters.studentName || null,
          appliedFilters.studentPhoneNumber || null,
          appliedFilters.startDate || null,
          appliedFilters.endDate || null
        );
        if (!response.success) {
          throw new Error(response.message || 'فشل في جلب الطلبات للتصدير');
        }
        const rows = response.data.data || [];
        allRows.push(...rows.map((row: Record<string, unknown>) => normalizeOrderRow(row)));
      }

      const sorted = sortOrderList(allRows, sortConfig);
      const headers = [
        'تاريخ الطلب',
        'الطالب',
        'الهاتف',
        'الدورة',
        'الحالة',
        'السعر النهائي',
        'الخصم',
        'السعر الأصلي',
        'كود الخصم',
        'مرجع الدفع',
      ];
      const csvRows = sorted.map((order) => [
        escapeCsvCell(formatDate(order.orderDate)),
        escapeCsvCell(order.studentName || '-'),
        escapeCsvCell(order.studentPhoneNumber || '-'),
        escapeCsvCell(stripSarPrice(order.courseName) || '-'),
        escapeCsvCell(getOrderStatusLabel(order.status)),
        escapeCsvCell(formatCurrency(order.netPrice)),
        escapeCsvCell(formatCurrency(order.discountAmount)),
        escapeCsvCell(formatCurrency(order.originalPrice)),
        escapeCsvCell(order.promoCode || '-'),
        escapeCsvCell(order.paymentReference 
          ? order.paymentReference.length > 10 
            ? order.paymentReference.substring(0, 10)
            : order.paymentReference
          : '-'),
      ]);

      const BOM = '\uFEFF';
      const csvContent =
        BOM + [headers.join(','), ...csvRows.map((row) => row.join(','))].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `orders-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error exporting orders:', e);
      setError('فشل في تصدير الطلبات. يرجى المحاولة مرة أخرى لاحقاً.');
    } finally {
      setExporting(false);
    }
  };

  if (!isModerator()) {
    return (
      <div className="p-4 md:p-6" dir="rtl">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">ليس لديك صلاحية لعرض الطلبات.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" dir="rtl">
      <h1 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">الطلبات</h1>

      {error && (
        <div className="mb-6 rounded-lg bg-red-50 p-4 border border-red-200">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">خطأ</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 rounded-lg bg-green-50 p-4 border border-green-200">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">نجح</h3>
              <p className="mt-1 text-sm text-green-700">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h2 className="text-lg font-semibold mb-4">الفلاتر</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">اسم الطالب</label>
            <input
              type="text"
              value={filters.studentName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, studentName: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              placeholder="البحث بالاسم"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">رقم الهاتف</label>
            <input
              type="text"
              value={filters.studentPhoneNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, studentPhoneNumber: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              placeholder="البحث بالهاتف"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">الحالة</label>
            <select
              value={filters.status}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">جميع الحالات</option>
              <option value="1">قيد الانتظار</option>
              <option value="2">مكتمل</option>
              <option value="3">ملغي</option>
              <option value="4">مسترجع</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">نوع الطلب</label>
            <select
              value={filters.type}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, type: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">جميع الأنواع</option>
              <option value="0">شراء مباشر</option>
              <option value="1">شراء محفظة</option>
              <option value="2">شحن بكود محفظة</option>
              <option value="3">استخدام محفظة</option>
              <option value="4">الأدمن شحن المحفظة</option>
              <option value="5">تسجيل من الأدمن</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">من تاريخ</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, startDate: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">إلى تاريخ</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, endDate: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={handleClearFilters}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
          >
            مسح
          </button>
          <button
            onClick={handleSearch}
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            بحث
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span className="font-medium">الإجمالي: {totalCount}</span>
          <button
            type="button"
            onClick={handleDownloadCsv}
            disabled={totalCount === 0 || exporting}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {exporting ? (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
            ) : (
              <ArrowDownTrayIcon className="h-5 w-5" aria-hidden />
            )}
            {exporting ? 'جاري التصدير...' : 'تحميل CSV'}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {([
                  { key: 'orderDate' as const, label: 'تاريخ الطلب' },
                  { key: 'studentName' as const, label: 'الطالب' },
                  { key: 'studentPhoneNumber' as const, label: 'الهاتف' },
                  { key: 'courseName' as const, label: 'الدورة' },
                  { key: 'type' as const, label: 'نوع الطلب' },
                  { key: 'status' as const, label: 'الحالة' },
                  { key: 'netPrice' as const, label: 'السعر النهائي' },
                  { key: 'discountAmount' as const, label: 'الخصم' },
                  { key: 'originalPrice' as const, label: 'السعر الأصلي' },
                  { key: 'promoCode' as const, label: 'كود الخصم' },
                  { key: 'paymentReference' as const, label: 'مرجع الدفع' },
                  { key: 'actions' as const, label: 'الإجراءات' }
                ] as const).map(({ key, label }) => (
                  <th
                    key={key}
                    onClick={() => key !== 'actions' && handleSort(key as keyof Order)}
                    className={`px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      key !== 'actions' ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''
                    }`}
                  >
                    {label}
                    {sortConfig && sortConfig.key === key as keyof Order && key !== 'actions' && (
                      <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={12} className="px-6 py-12">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : sortedOrders.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-6 py-4 text-center text-gray-500">
                    لا توجد طلبات مطابقة للبحث
                  </td>
                </tr>
              ) : (
                sortedOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{formatDate(order.orderDate)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{order.studentName || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{order.studentPhoneNumber || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{stripSarPrice(order.courseName) || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getOrderTypeColor(order.type)}`}>
                        {getOrderTypeLabel(order.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getOrderStatusColor(order.status)}`}>
                        {getOrderStatusLabel(order.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">{formatCurrency(order.netPrice)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{formatCurrency(order.discountAmount)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{formatCurrency(order.originalPrice)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{order.promoCode || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {order.paymentReference 
                        ? order.paymentReference.length > 10 
                          ? order.paymentReference.substring(0, 10)
                          : order.paymentReference
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <select
                        value={order.status}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleUpdateStatus(order.id, parseInt(e.target.value) as OrderStatus)}
                        className="px-2 py-1 border rounded text-xs"
                        disabled={order.status === OrderStatus.Completed || order.status === OrderStatus.Refunded}
                      >
                        <option value={OrderStatus.Pending}>قيد الانتظار</option>
                        <option value={OrderStatus.Completed}>مكتمل</option>
                        <option value={OrderStatus.Cancelled}>ملغي</option>
                        <option value={OrderStatus.Refunded}>مسترجع</option>
                      </select>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && totalPages > 0 && (
          <div className="px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="mb-4 sm:mb-0">
              <p className="text-sm text-gray-700">
                عرض <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> إلى{' '}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, totalCount)}
                </span>{' '}
                من <span className="font-medium">{totalCount}</span> نتيجة
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span>&laquo;</span>
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span>&lsaquo;</span>
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => 
                    page === 1 || 
                    page === totalPages || 
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  )
                  .map((page, index, array) => {
                    const prevPage = array[index - 1];
                    if (index > 0 && prevPage !== page - 1) {
                      return [
                        <span key={`ellipsis-${prevPage}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>,
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                            currentPage === page ? 'z-10 bg-primary text-white border-primary' : 'bg-white text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ];
                    }
                    
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                          currentPage === page ? 'z-10 bg-primary text-white border-primary' : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  }).flat()}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span>&rsaquo;</span>
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span>&raquo;</span>
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;
 